import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label
} from 'reactstrap';

import { Btn } from '../../../../AbstractElements';
import Select from 'react-select';
import axios from 'axios';
import config from '../../../../config';

import TenantAuditSummary from './TenantAuditSummary';
import Widgets1 from '../../../Common/CommonWidgets/Widgets1';

const AuditDashboard = () => {

  const { t } = useTranslation();
  const concessionaire_id = localStorage.getItem('concessionaireId');
  const property_access = localStorage.getItem('propertyAccess');
  const token = localStorage.getItem('sessiontoken');

  const navigate = useNavigate();
  const location = useLocation();

  const configheader = {
    headers: { Authorization: token }
  };

  const [properties, setProperties] = useState([]);
  const [property_id, setPropertyId] = useState('');
  const [propertyName, setPropertyName] = useState('');

  const [auditPeriodId, setAuditPeriodId] = useState(null);
  const [tenantAudits, setTenantAudits] = useState([]);

  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  const [propertyError, setPropertyError] = useState('');
  const [monthError, setMonthError] = useState('');

  const getPreviousMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getPreviousMonth());

  const getMonth = () => Number(selectedMonth.split("-")[1]);
  const getYear = () => Number(selectedMonth.split("-")[0]);

  const formatMonthYear = (ym) => {
    const [year, month] = ym.split("-");
    return new Date(year, month - 1).toLocaleString("en-US", {
      month: "long",
      year: "numeric"
    });
  };

  /* -------------------- PROPERTY OPTIONS -------------------- */

  const propertyOptions = useMemo(
    () =>
      properties.map(p => ({
        value: p._id,
        label: `${p.property_name} (${p.property_mall_code})`
      })),
    [properties]
  );

  /* -------------------- STATS -------------------- */

  const stats = useMemo(() => {

    const result = {
      total: tenantAudits.length,
      matched: 0,
      notMatched: 0,
      reconciled: 0,
      pending: 0
    };

    tenantAudits.forEach(t => {
      if (t.status === "MATCHED") result.matched++;
      if (t.status === "NOT_MATCHED") result.notMatched++;
      if (t.status === "RECONCILED") result.reconciled++;
      if (t.status === "PENDING") result.pending++;
    });

    return result;

  }, [tenantAudits]);

  /* -------------------- RESTORE STATE FROM UPLOAD PAGE -------------------- */

  useEffect(() => {

    if (!location.state) return;

    const { property_id, property_name, year, month } = location.state;

    if (property_id) {

      setPropertyId(property_id);
      setPropertyName(property_name);

      const m = String(month).padStart(2, "0");
      setSelectedMonth(`${year}-${m}`);

    }

  }, [location.state]);

  /* -------------------- LOAD PROPERTIES -------------------- */

  useEffect(() => {

    axios
      .post(
        `${config.hostname}/property/getpropertybyconcessionaire`,
        {
          property_concessionaire_id: concessionaire_id,
          property_id: property_access
        },
        configheader
      )
      .then(res => setProperties(res.data))
      .catch(console.error);

  }, []);

  /* -------------------- FETCH AUDIT DATA -------------------- */

  const fetchAuditData = () => {

    if (!property_id) {
      setTenantAudits([]);
      setAuditPeriodId(null);
      return;
    }

    setLoading(true);

    axios
      .post(
        `${config.hostname}/audit/getauditperiod`,
        {
          concessionaire_id,
          property_id,
          month: getMonth(),
          year: getYear()
        },
        configheader
      )
      .then(res => {

        setAuditPeriodId(res.data.audit_period_id || null);
        setTenantAudits(res.data.tenantAudits || []);
        setIsLocked(res.data.is_locked);

      })
      .catch(() => {
        setTenantAudits([]);
        setAuditPeriodId(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAuditData();
  }, [property_id, selectedMonth]);

  /* -------------------- HANDLERS -------------------- */

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleUploadNav = () => {

    let valid = true;

    if (!property_id) {
      setPropertyError(t('Select a property'));
      valid = false;
    } else {
      setPropertyError("");
    }

    if (!selectedMonth) {
      setMonthError(t('Select a month'));
      valid = false;
    } else {
      setMonthError("");
    }

    if (!valid) return;

    navigate(`${process.env.PUBLIC_URL}/admin/audit/uploadmonthlysales/default`, {
      state: {
        property_id,
        property_name: propertyName,
        month: getMonth(),
        year: getYear(),
        audit_period_id: auditPeriodId
      }
    });

  };

  const handleStatClick = status => {
    setActiveStatusFilter(prev => (prev === status ? null : status));
  };

  /* -------------------- UI -------------------- */

  return (

    <Fragment>

      <Container fluid>

        {/* FILTER CARD */}

        <Card>

          <CardBody>

            <Row className="align-items-end">

              <Col md="4">

                <FormGroup style={{ position: "relative" }}>

                  <Label>{t('Property')}</Label>

                  <Select
                    placeholder={t('Select Property')}
                    isClearable
                    value={propertyOptions.find(o => o.value === property_id) || null}
                    options={propertyOptions}
                    onChange={opt => {

                      const id = opt?.value || "";
                      setPropertyId(id);

                      const selected = properties.find(p => p._id === id);
                      setPropertyName(selected?.property_name || "");

                      setActiveStatusFilter(null);
                      setPropertyError("");

                    }}
                  />
                  {propertyError && (
                    <div style={{ position: "absolute", top: "70px", left: "0", color: "red", fontSize: "12px" }} >
                      {propertyError}
                    </div>
                  )}
                </FormGroup>

              </Col>

              <Col md="4">
                <FormGroup style={{ position: "relative" }}>
                  <Label>{t('Month')}</Label>
                  <input
                    className="form-control"
                    type="month"
                    value={selectedMonth}
                    max={getPreviousMonth()}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setMonthError("");
                    }}
                  />

                  {monthError && (
                    <div style={{ position: "absolute", top: "70px", left: "0", color: "red", fontSize: "12px" }} >
                      {monthError}
                    </div>
                  )}

                </FormGroup>
              </Col>

              <Col className="ms-auto text-end">

                <FormGroup>

                  <Label>&nbsp;</Label>

                  <div>

                    <Btn
                      attrBtn={{
                        color: 'primary',
                        onClick: handleUploadNav
                      }}
                    >
                      {t('Upload Sales')}
                    </Btn>

                  </div>

                </FormGroup>

              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* KPI WIDGETS */}

        <Card>

          <Row className="mt-3 g-3">

            <Col>
              <Widgets1 data={{ title: t('Total Tenants'), total: stats.total, color: 'primary', icon: 'customers' }} />
            </Col>

            <Col>
              <div onClick={() => handleStatClick('MATCHED')}>
                <Widgets1 data={{ title: t('Matched'), total: stats.matched, color: 'success', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('NOT_MATCHED')}>
                <Widgets1 data={{ title: t('Not Matched'), total: stats.notMatched, color: 'secondary', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('RECONCILED')}>
                <Widgets1 data={{ title: t('Reconciled'), total: stats.reconciled, color: 'warning', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('PENDING')}>
                <Widgets1 data={{ title: t('Pending'), total: stats.pending, color: 'info', icon: 'customers' }} />
              </div>
            </Col>

          </Row>

        </Card>

        {/* SUMMARY TABLE */}

        <Card className="mt-3">

          <CardHeader>
            <h5>{t('Tenant Audit Summary')} – {formatMonthYear(selectedMonth)}</h5>
          </CardHeader>

          <CardBody>

            <TenantAuditSummary
              tenantAudits={tenantAudits}
              loading={loading}
              propertySelected={!!property_id}
              monthLabel={getMonth()}
              selectedYear={getYear()}
              isLocked={isLocked}
              auditPeriodId={auditPeriodId}
              activeStatusFilter={activeStatusFilter}
              setActiveStatusFilter={setActiveStatusFilter}
              onRefresh={fetchAuditData}
            />

          </CardBody>

        </Card>

      </Container>

    </Fragment>
  );
};

export default AuditDashboard;